// Discount Logic: Coupon Code vs Tiered Discount Comparison
// This script handles the logic to apply the better discount between coupon codes and tiered discounts

class DiscountManager {
  constructor() {
    console.log('🔧 DiscountManager: Initializing...');
    this.tieredDiscounts = [
      { tier: 750, discount: 200, message: "BLACK FRIDAY Spend And Save" },
      { tier: 1500, discount: 100, message: "BLACK FRIDAY Spend And Save" },
      { tier: 5000, discount: 50, message: "BLACK FRIDAY Spend And Save" },
      { tier: 10000, discount: 100, message: "BLACK FRIDAY Spend And Save" }
    ];
    console.log('📊 DiscountManager: Tiered discounts configured:', this.tieredDiscounts);
  }

  // Calculate what tiered discount would be applied
  calculateTieredDiscount(cartTotal) {
    console.log('🧮 calculateTieredDiscount: Cart total received:', cartTotal);
    
    // Sort tiers by discount amount (highest first)
    const sortedTiers = this.tieredDiscounts.sort((a, b) => b.discount - a.discount);
    console.log('📈 calculateTieredDiscount: Sorted tiers (highest first):', sortedTiers);
    
    for (let tier of sortedTiers) {
      console.log(`🔍 calculateTieredDiscount: Checking tier ${tier.tier} ($${tier.discount}) against cart total $${cartTotal}`);
      if (cartTotal >= tier.tier) {
        console.log(`✅ calculateTieredDiscount: Cart total $${cartTotal} >= tier $${tier.tier}, applying discount $${tier.discount}`);
        return {
          amount: tier.discount,
          message: tier.message,
          tier: tier.tier
        };
      }
    }
    console.log('❌ calculateTieredDiscount: No tiered discount applicable for cart total $' + cartTotal);
    return null;
  }

  // Get coupon code discount amount from cart
  getCouponDiscount(cart) {
    console.log('🎫 getCouponDiscount: Cart object received:', cart);
    
    // Handle case where cart is a form element (checkout page)
    if (cart.tagName === 'FORM') {
      console.log('🎫 getCouponDiscount: Cart is a form element, looking for cart data elsewhere...');
      
      // Try to get cart data from various sources
      if (window.cart && typeof window.cart === 'object') {
        console.log('🎫 getCouponDiscount: Found cart data in window.cart:', window.cart);
        return this.getCouponDiscount(window.cart);
      }
      
      // Try to get from cart attributes
      if (window.cart && window.cart.attributes) {
        console.log('🎫 getCouponDiscount: Found cart attributes:', window.cart.attributes);
        if (window.cart.attributes.coupon_code) {
          return window.cart.attributes.coupon_code;
        }
      }
      
      // Try to get from cart metafields
      if (window.cart && window.cart.metafields) {
        console.log('🎫 getCouponDiscount: Found cart metafields:', window.cart.metafields);
      }
      
      return 0;
    }
    
    // This would need to be implemented based on how your cart stores coupon information
    // You might need to check cart attributes, metafields, or other methods
    if (cart.discount_codes && cart.discount_codes.length > 0) {
      console.log('🎫 getCouponDiscount: Found discount codes:', cart.discount_codes);
      const couponAmount = cart.discount_codes[0].amount;
      console.log('🎫 getCouponDiscount: First coupon amount:', couponAmount);
      return couponAmount;
    }
    
    // Try alternative methods to find coupon information
    console.log('🔍 getCouponDiscount: Checking alternative coupon sources...');
    
    if (cart.discount_codes) {
      console.log('🔍 getCouponDiscount: cart.discount_codes exists but is empty or undefined');
    }
    
    if (cart.attributes && cart.attributes.coupon_code) {
      console.log('🔍 getCouponDiscount: Found coupon in cart attributes:', cart.attributes.coupon_code);
      return cart.attributes.coupon_code;
    }
    
    if (cart.total_discount && cart.total_discount > 0) {
      console.log('🔍 getCouponDiscount: Found total discount:', cart.total_discount);
      return cart.total_discount;
    }
    
    console.log('❌ getCouponDiscount: No coupon discount found');
    return 0;
  }

  // Compare and apply the better discount
  applyBetterDiscount(cart) {
    console.log('🚀 applyBetterDiscount: Starting discount comparison...');
    console.log('📦 applyBetterDiscount: Full cart object:', cart);
    
    // Handle case where cart is a form element (checkout page)
    if (cart.tagName === 'FORM') {
      console.log('🚀 applyBetterDiscount: Cart is a form element, looking for cart data elsewhere...');
      
      // Try to get actual cart data
      if (window.cart && typeof window.cart === 'object' && !window.cart.tagName) {
        console.log('🚀 applyBetterDiscount: Found actual cart data in window.cart, using that instead');
        return this.applyBetterDiscount(window.cart);
      }
      
      // Try to get cart total from form or page elements
      const cartTotalElement = document.querySelector('[data-cart-total], .cart-total, .cart__total, .cart-total-price');
      if (cartTotalElement) {
        console.log('🚀 applyBetterDiscount: Found cart total element:', cartTotalElement);
        const cartTotalText = cartTotalElement.textContent || cartTotalElement.innerText;
        console.log('🚀 applyBetterDiscount: Cart total text:', cartTotalText);
        
        // Extract number from text (remove $ and commas)
        const cartTotal = parseFloat(cartTotalText.replace(/[$,]/g, ''));
        console.log('🚀 applyBetterDiscount: Parsed cart total:', cartTotal);
        
        if (!isNaN(cartTotal)) {
          return this.processDiscounts(cartTotal, 0);
        }
      }
      
      console.log('❌ applyBetterDiscount: Could not determine cart total from form');
      return 'none';
    }
    
    const cartTotal = cart.total_price / 100; // Convert from cents to dollars
    console.log('💰 applyBetterDiscount: Cart total (converted from cents):', cartTotal);
    
    if (isNaN(cartTotal)) {
      console.log('❌ applyBetterDiscount: Cart total is NaN, cannot process discounts');
      return 'none';
    }
    
    const tieredDiscount = this.calculateTieredDiscount(cartTotal);
    console.log('🎯 applyBetterDiscount: Tiered discount result:', tieredDiscount);
    
    const couponDiscount = this.getCouponDiscount(cart);
    console.log('🎫 applyBetterDiscount: Coupon discount result:', couponDiscount);

    return this.processDiscounts(cartTotal, couponDiscount, tieredDiscount);
  }

  // Process discounts with the given values
  processDiscounts(cartTotal, couponDiscount, tieredDiscount = null) {
    console.log('⚖️ processDiscounts: Comparison summary:');
    console.log('   - Cart Total: $' + cartTotal);
    console.log('   - Tiered Discount: $' + (tieredDiscount ? tieredDiscount.amount : 0));
    console.log('   - Coupon Discount: $' + couponDiscount);

    if (couponDiscount > 0 && tieredDiscount) {
      console.log('🔄 processDiscounts: Both discounts available, comparing...');
      if (couponDiscount > tieredDiscount.amount) {
        // Coupon is better - remove tiered discount, keep coupon
        console.log('🏆 processDiscounts: Coupon discount ($' + couponDiscount + ') is better than tiered discount ($' + tieredDiscount.amount + ')');
        console.log('🎯 processDiscounts: Applying coupon, removing tiered discount');
        this.removeTieredDiscount();
        return 'coupon';
      } else {
        // Tiered discount is better - remove coupon, keep tiered
        console.log('🏆 processDiscounts: Tiered discount ($' + tieredDiscount.amount + ') is better than coupon discount ($' + couponDiscount + ')');
        console.log('🎯 processDiscounts: Applying tiered discount, removing coupon');
        this.removeCouponDiscount();
        return 'tiered';
      }
    } else if (tieredDiscount) {
      // No coupon, apply tiered discount
      console.log('🎯 processDiscounts: No coupon available, applying tiered discount');
      return 'tiered';
    } else if (couponDiscount > 0) {
      // No tiered discount, apply coupon
      console.log('🎯 processDiscounts: No tiered discount available, applying coupon');
      return 'coupon';
    }

    console.log('❌ processDiscounts: No discounts available');
    return 'none';
  }

  // Remove tiered discount display/messaging
  removeTieredDiscount() {
    console.log('🗑️ removeTieredDiscount: Removing tiered discount elements...');
    
    // Remove any tiered discount messaging from the page
    const tieredElements = document.querySelectorAll('[data-discount-type="tiered"]');
    console.log('🔍 removeTieredDiscount: Found tiered elements with data-discount-type="tiered":', tieredElements.length);
    tieredElements.forEach((element, index) => {
      console.log('🗑️ removeTieredDiscount: Hiding tiered element', index, ':', element);
      element.style.display = 'none';
    });

    // Remove tiered discount from cart totals if displayed
    const tieredTotalElements = document.querySelectorAll('.tiered-discount-total');
    console.log('🔍 removeTieredDiscount: Found tiered total elements with class "tiered-discount-total":', tieredTotalElements.length);
    tieredTotalElements.forEach((element, index) => {
      console.log('🗑️ removeTieredDiscount: Hiding tiered total element', index, ':', element);
      element.style.display = 'none';
    });
    
    // Also try to find and hide any elements with "BLACK FRIDAY" text
    const blackFridayElements = document.querySelectorAll('*');
    let foundBlackFriday = 0;
    blackFridayElements.forEach(element => {
      if (element.textContent && element.textContent.includes('BLACK FRIDAY Spend And Save')) {
        console.log('🔍 removeTieredDiscount: Found BLACK FRIDAY text in element:', element);
        foundBlackFriday++;
        // Don't hide by default, just log for debugging
      }
    });
    console.log('🔍 removeTieredDiscount: Total elements with BLACK FRIDAY text:', foundBlackFriday);
  }

  // Remove coupon discount display/messaging
  removeCouponDiscount() {
    console.log('🗑️ removeCouponDiscount: Removing coupon discount elements...');
    
    // Remove any coupon discount messaging from the page
    const couponElements = document.querySelectorAll('[data-discount-type="coupon"]');
    console.log('🔍 removeCouponDiscount: Found coupon elements with data-discount-type="coupon":', couponElements.length);
    couponElements.forEach((element, index) => {
      console.log('🗑️ removeCouponDiscount: Hiding coupon element', index, ':', element);
      element.style.display = 'none';
    });

    // Remove coupon discount from cart totals if displayed
    const couponTotalElements = document.querySelectorAll('.coupon-discount-total');
    console.log('🔍 removeCouponDiscount: Found coupon total elements with class "coupon-discount-total":', couponTotalElements.length);
    couponTotalElements.forEach((element, index) => {
      console.log('🗑️ removeCouponDiscount: Hiding coupon total element', index, ':', element);
      element.style.display = 'none';
    });
  }

  // Initialize discount management
  init() {
    console.log('🚀 DiscountManager.init: Starting initialization...');
    
    // Listen for cart updates
    if (typeof Shopify !== 'undefined' && Shopify.onCartUpdate) {
      console.log('🔄 DiscountManager.init: Shopify.onCartUpdate hook found, setting up listener...');
      Shopify.onCartUpdate = (cart) => {
        console.log('🔄 Shopify.onCartUpdate: Cart updated, running discount logic...');
        this.applyBetterDiscount(cart);
      };
    } else {
      console.log('⚠️ DiscountManager.init: Shopify.onCartUpdate not available');
    }

    // Listen for cart updates from cart-update.js
    console.log('🔍 DiscountManager.init: Setting up cart update listener...');
    document.addEventListener('cartUpdated', (event) => {
      console.log('🔄 cartUpdated event received:', event);
      if (event.detail && event.detail.cart) {
        console.log('🔄 cartUpdated: Cart data received, applying discount logic...');
        this.applyBetterDiscount(event.detail.cart);
      }
    });

    // Also check on page load
    if (typeof window.cart !== 'undefined') {
      console.log('🔄 DiscountManager.init: window.cart found, applying discount logic...');
      this.applyBetterDiscount(window.cart);
    } else {
      console.log('⚠️ DiscountManager.init: window.cart not available');
    }

    // Try to find cart information in other ways
    console.log('🔍 DiscountManager.init: Searching for cart information...');
    
    // Check if we're on a cart page or checkout page
    if (window.location.pathname.includes('/cart') || window.location.pathname.includes('/checkout')) {
      console.log('🛒 DiscountManager.init: On cart/checkout page, looking for cart data...');
      
      // Try to find cart data in various places
      setTimeout(() => {
        this.findCartData();
      }, 1000);
    }

    // Monitor for cart data changes
    this.monitorCartData();

    console.log('✅ DiscountManager.init: Initialization complete');
  }

  // Monitor for cart data changes
  monitorCartData() {
    console.log('🔍 monitorCartData: Setting up cart data monitoring...');
    
    // Method 1: Monitor cart-update.js data
    let cartUpdateCount = 0;
    const originalConsoleLog = console.log;
    console.log = function(...args) {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('Updating cart count with cart data:')) {
        console.log('🔄 monitorCartData: Cart update detected in console!');
        if (args[1] && typeof args[1] === 'object') {
          console.log('🔄 monitorCartData: Cart data found:', args[1]);
          // Trigger discount logic with this cart data
          if (window.discountManager) {
            window.discountManager.applyBetterDiscount(args[1]);
          }
        }
      }
      originalConsoleLog.apply(console, args);
    };

    // Method 2: Check for cart data periodically
    setInterval(() => {
      // Look for cart data in various places
      if (window.cart && typeof window.cart === 'object' && window.cart.total_price) {
        console.log('🔄 monitorCartData: Found cart data in window.cart:', window.cart);
        this.applyBetterDiscount(window.cart);
      }
      
      // Look for cart data in cart-update.js
      const cartUpdateScripts = document.querySelectorAll('script[src*="cart-update.js"]');
      if (cartUpdateScripts.length > 0) {
        console.log('🔍 monitorCartData: cart-update.js found, monitoring for data...');
      }
    }, 2000);

    console.log('✅ monitorCartData: Cart monitoring setup complete');
  }

  // Find cart data from various sources
  findCartData() {
    console.log('🔍 findCartData: Searching for cart data...');
    
    // Method 1: Check for cart total elements
    const cartTotalElements = document.querySelectorAll('[data-cart-total], .cart-total, .cart__total, .cart-total-price, .cart__total-price');
    console.log('🔍 findCartData: Found potential cart total elements:', cartTotalElements.length);
    
    if (cartTotalElements.length > 0) {
      cartTotalElements.forEach((element, index) => {
        console.log('🔍 findCartData: Cart total element', index, ':', element);
        const cartTotalText = element.textContent || element.innerText;
        console.log('🔍 findCartData: Cart total text', index, ':', cartTotalText);
      });
    }

    // Method 2: Check for cart items count
    const cartItemElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart__count');
    console.log('🔍 findCartData: Found potential cart count elements:', cartItemElements.length);

    // Method 3: Check for cart subtotal
    const cartSubtotalElements = document.querySelectorAll('[data-cart-subtotal], .cart-subtotal, .cart__subtotal');
    console.log('🔍 findCartData: Found potential cart subtotal elements:', cartSubtotalElements.length);

    // Method 4: Check for cart attributes in data attributes
    const cartDataElements = document.querySelectorAll('[data-cart-attributes]');
    console.log('🔍 findCartData: Found potential cart data elements:', cartDataElements.length);

    // Method 5: Check for any elements with cart-related classes
    const allCartElements = document.querySelectorAll('*[class*="cart"]');
    console.log('🔍 findCartData: Found elements with cart-related classes:', allCartElements.length);

    // Method 6: Check for discount elements
    const discountElements = document.querySelectorAll('*[class*="discount"], *[class*="coupon"]');
    console.log('🔍 findCartData: Found potential discount elements:', discountElements.length);

    // Method 7: Check for BLACK FRIDAY text
    const blackFridayElements = document.querySelectorAll('*');
    let foundBlackFriday = 0;
    blackFridayElements.forEach(element => {
      if (element.textContent && element.textContent.includes('BLACK FRIDAY')) {
        console.log('🔍 findCartData: Found BLACK FRIDAY text in element:', element);
        foundBlackFriday++;
      }
    });
    console.log('🔍 findCartData: Total elements with BLACK FRIDAY text:', foundBlackFriday);

    // Method 8: Check for any price elements
    const priceElements = document.querySelectorAll('*[class*="price"], *[class*="total"]');
    console.log('🔍 findCartData: Found potential price elements:', priceElements.length);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('🌐 DOMContentLoaded: Setting up DiscountManager...');
  window.discountManager = new DiscountManager();
  window.discountManager.init();
  console.log('✅ DOMContentLoaded: DiscountManager setup complete');
});

// Also try to initialize on window load as backup
window.addEventListener('load', function() {
  console.log('🌐 window.load: Backup initialization for DiscountManager...');
  if (!window.discountManager) {
    console.log('🔄 window.load: Creating new DiscountManager instance...');
    window.discountManager = new DiscountManager();
    window.discountManager.init();
  }
  console.log('✅ window.load: Backup initialization complete');
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DiscountManager;
}

// Global debug function
window.debugDiscountManager = function() {
  console.log('🔧 Debug: DiscountManager instance:', window.discountManager);
  if (window.discountManager) {
    console.log('🔧 Debug: Tiered discounts:', window.discountManager.tieredDiscounts);
    console.log('🔧 Debug: Current cart state:', window.cart);
  }
  return window.discountManager;
};

// Test discount logic with sample cart data
window.testDiscountLogic = function() {
  console.log('🧪 testDiscountLogic: Testing discount logic...');
  
  if (!window.discountManager) {
    console.log('❌ testDiscountLogic: DiscountManager not found');
    return;
  }

  // Test with the cart data we found in console
  const testCart = {
    total_price: 89945, // $899.45 in cents
    original_total_price: 109945, // $1099.45 in cents
    attributes: {
      site: 'Lammles'
    }
  };

  console.log('🧪 testDiscountLogic: Testing with cart data:', testCart);
  const result = window.discountManager.applyBetterDiscount(testCart);
  console.log('🧪 testDiscountLogic: Result:', result);
  
  return result;
};

// Test with different cart totals
window.testDiscountTiers = function() {
  console.log('🧪 testDiscountTiers: Testing different cart totals...');
  
  if (!window.discountManager) {
    console.log('❌ testDiscountTiers: DiscountManager not found');
    return;
  }

  const testTotals = [500, 750, 1000, 1500, 5000, 10000];
  
  testTotals.forEach(total => {
    const testCart = {
      total_price: total * 100, // Convert to cents
      attributes: { site: 'Lammles' }
    };
    
    console.log(`🧪 testDiscountTiers: Testing $${total} cart...`);
    const result = window.discountManager.applyBetterDiscount(testCart);
    console.log(`🧪 testDiscountTiers: $${total} cart result:`, result);
  });
};

console.log('📜 discount-logic.js: Script loaded successfully');
console.log('🔧 Debug functions available:');
console.log('  - window.debugDiscountManager() - Show current state');
console.log('  - window.testDiscountLogic() - Test with sample cart data');
console.log('  - window.testDiscountTiers() - Test different cart totals');
